import React, { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Reusable searchable combobox using shadcn Command + Popover.
 * items: [{ value: string, label: string, disabled?: bool }]
 */
export default function Combobox({
  items,
  value,
  onSelect,
  placeholder = "Select an option…",
  emptyText = "No results.",
  triggerTestId,
  itemTestIdPrefix,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const selectedLabel = useMemo(
    () => items.find((i) => i.value === value)?.label,
    [items, value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={triggerTestId}
          className={cn(
            "w-full flex items-start justify-between gap-2 rounded-2xl border border-slate-300 bg-white px-4 sm:px-5 py-3 text-left",
            "text-sm font-medium text-slate-800 hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2",
            "transition-colors active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 min-h-[48px]"
          )}
        >
          <span
            className={cn(
              "flex-1 whitespace-normal break-words leading-snug",
              !selectedLabel && "text-slate-400 font-normal"
            )}
          >
            {selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-60 mt-0.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(15,23,42,0.08)]"
        align="start"
      >
        <Command className="bg-white">
          <CommandInput placeholder="Type to search…" className="text-sm" />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((it) => (
                <CommandItem
                  key={it.value}
                  // Prefix cmdk's internal value so it is never a bare numeric
                  // string. cmdk treats the string "0" as falsy in some click
                  // paths, making an item with id 0 silently unclickable.
                  // Prefixing sidesteps that edge case AND still gives every
                  // item a unique key (avoiding label-collision collapses).
                  value={`opt-${it.value}`}
                  keywords={[it.label]}
                  disabled={it.disabled}
                  data-testid={itemTestIdPrefix ? `${itemTestIdPrefix}-${it.value}` : undefined}
                  onSelect={() => {
                    onSelect(it.value);
                    setOpen(false);
                  }}
                  className="text-sm items-start py-2.5 data-[disabled=true]:line-through data-[disabled=true]:opacity-50"
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0 mt-0.5", value === it.value ? "opacity-100" : "opacity-0")} />
                  <span className="flex-1 whitespace-normal break-words leading-snug">{it.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
